/**
 * Service to copy the currently active timeline element into the editor clipboard.
 *
 * Reads the active element from the video creator store, serializes it via the
 * editor Element model's toJSON(), and stores the result in the VideoEditor
 * element clipboard. Layout elements and multi-selection are not supported.
 *
 * @module services/timeline/editing/CopyElementService
 */

import { err, ok, type Result } from '@src/services/common/Result';
import { getElementMetaObject } from '@src/utility/extraElementData';
import { LogManager } from '@src/utility/Logger';

import type { ElementClipboardEntry } from './ElementClipboardTypes';
import type { ElementState } from '@src/renderer/ElementState';

// ---------------------------------------------------------------------------
// Structural interfaces (narrow contracts — decoupled from concrete stores)
// ---------------------------------------------------------------------------

/** Subset of VideoCreatorStore needed by CopyElementService */
export interface CopyVideoCreatorStoreLike {
  activeElementIds: string[];
  getRealActiveElement: () => ElementState | undefined;
  currentVideo?: { extraElementData?: Record<string, any> };
}

/** Subset of EditorStore needed by CopyElementService */
export interface CopyEditorStoreLike {
  getElementById: (
    id: string,
  ) => { toJSON: () => Record<string, any> } | undefined;
}

/** Clipboard write interface (implemented by VideoEditor) */
export interface ClipboardWriter {
  setElementClipboard: (entry: ElementClipboardEntry | null) => void;
}

export interface CopyElementServiceDeps {
  videoCreator: CopyVideoCreatorStoreLike;
  editorStore: CopyEditorStoreLike;
  clipboardHolder: ClipboardWriter;
}

/**
 * Copies the single active timeline element into the editor clipboard buffer.
 *
 * Guards:
 * - Exactly one element must be selected (multi-select → noop).
 * - Layout elements are not supported (presence of layoutAssociatedIds → noop).
 *
 * Does NOT modify the timeline or active selection.
 */
export class CopyElementService {
  private readonly _videoCreator: CopyVideoCreatorStoreLike;
  private readonly _editorStore: CopyEditorStoreLike;
  private readonly _clipboard: ClipboardWriter;
  private readonly _logger = LogManager.getLogger('service:CopyElementService');

  constructor(deps: CopyElementServiceDeps) {
    this._videoCreator = deps.videoCreator;
    this._editorStore = deps.editorStore;
    this._clipboard = deps.clipboardHolder;
  }

  /**
   * Copies the active element to the clipboard.
   *
   * @returns The clipboard entry on success, or an error describing why copy failed.
   */
  copy(): Result<ElementClipboardEntry> {
    // Guard: exactly one element selected
    const { activeElementIds } = this._videoCreator;
    if (activeElementIds.length !== 1) {
      return err(
        'PRECONDITION',
        activeElementIds.length === 0
          ? 'No element selected'
          : 'Multiple elements selected — copy supports single element only',
      );
    }

    // Resolve through layout parent (getRealActiveElement)
    const elementState = this._videoCreator.getRealActiveElement();
    if (!elementState) {
      return err('NOT_FOUND', 'Active element could not be resolved');
    }

    const originalId = elementState.source.id as string;

    // Guard: layout elements not supported
    const meta = getElementMetaObject(
      this._videoCreator.currentVideo,
      originalId,
    );
    if (meta?.layoutAssociatedIds) {
      return err(
        'PRECONDITION',
        'Layout elements are not supported for copy/paste',
      );
    }

    // Look up the editor Element model to get a clean serialized form
    const editorElement = this._editorStore.getElementById(originalId);
    if (!editorElement) {
      this._logger.warn('Element not found in editor model', { originalId });
      return err('NOT_FOUND', 'Element not found in editor');
    }

    // Serialize and strip `id` so paste generates a fresh one
    const source = { ...editorElement.toJSON() };
    delete source.id;

    // Deep-clone extra element data (if any) so clipboard is independent
    const extraElementData = meta ? { ...meta } : undefined;

    const entry: ElementClipboardEntry = {
      source,
      extraElementData,
      originalTrack: elementState.source.track as number,
      originalId,
    };

    this._clipboard.setElementClipboard(entry);
    this._logger.info('Element copied to clipboard', {
      originalId,
      type: source.type,
    });

    return ok(entry);
  }
}
